import React, { Component } from 'react';
import XMTooltipButton from '../XMTooltipButton';
import { Link } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { apiConfigHelper } from '../../apiConfig';

/**
 * The store this toolbar belongs to, or undefined when the page is not a store's.
 *
 * Segment 2 of the path is only a store id under /stores/. Read unconditionally it also returned 'adverts' on
 * /manage-apps/adverts and 'apps' on /manage-apps/apps, which is truthy — so the whole store toolbar rendered
 * on Manage Apps, with links to /stores/adverts/backup and a 'Store ID:' taken from whichever store happened to
 * be left in the reducer (UN-3372 item 6). It did the same on /stores/backup and the other store-less /stores/*
 * routes.
 *
 * The loaded store has to agree, not just the shape of the URL: state.store.one is what every label and link is
 * built from, so rendering them while it names a different store is the same defect wearing a better path.
 * Mid-navigation that briefly yields nothing, which is the right answer — a toolbar for the store you just left
 * is worse than no toolbar at all.
 *
 * `loadedStoreId` is the store's DATABASE id (store.one.id), not its business storeId. Those are two different
 * identifiers and the route carries the first: StoreTableRow calls btn.action(store.id) and every handler in
 * views/Stores navigates to `/stores/${store.id}/...`. Backups and Settings say the same thing a second way,
 * reading `router.params.storeId || store.one.id` as interchangeable. Comparing the segment against
 * store.one.storeId instead would never match, and would hide the toolbar on every store page (review on #284).
 */
export const storeIdForPath = (pathname, store) => {
  // Takes the whole store slice, not an id, so that WHICH id is the right one is covered by this function's
  // tests rather than left at the call site — passing store.one.storeId here type-checks, reads plausibly and
  // matches nothing, which is precisely the regression the review caught.
  const loadedStoreId = store && store.one && store.one.id;
  if (!pathname || !pathname.startsWith('/stores/') || !loadedStoreId) return undefined;
  const segment = pathname.split('/')[2];
  return segment && segment === String(loadedStoreId) ? segment : undefined;
};

@connect((state) => {
  return {
    backup: state.backup,
    store: state.store,
    erpType: state.erpType,
    operator: state.operator,
    dataCentre: state.dataCentre,
    user: state.user,
  };
})
class BreadcrumbIcon extends Component {
  constructor(props) {
    super(props);
  }

  async componentDidMount() {
    const { router, currentStore, fetchSources, fetchRequestStatus } = this.props;
    if (currentStore && currentStore.storeId) {
      const sources = await fetchSources(currentStore.storeId);
      if (isEmpty(sources)) {
        router.navigate(`${router.location.pathname}/connection`);
      } else {
        await fetchRequestStatus(currentStore.storeId);
      }
    }
  }

  getStoreId(pathname, store) {
    return storeIdForPath(pathname, store);
  }

  render () {
    const { pathname, store, version, syncStoresStatus, user } = this.props;
    const storeId = this.getStoreId(pathname, store);
    const gatewayBaseUrl = apiConfigHelper.getDataCentreUrlForGateway();
    const isPartner = user.selected && user.selected.role === 'PARTNER';
    const btns = [
      {
        icon: 'icon-chart',
        url: `/stores/${storeId}/stats`,
        tooltip: 'Stats',
      },
      {
        icon: 'icon-cloud-download',
        url: `/stores/${storeId}/backup`,
        tooltip: 'Backup',
      },
      {
        icon: 'icon-loop',
        url: `/stores/${storeId}/synchronization`,
        tooltip: 'Sync',
        isSync: true,
      },
      {
        icon: 'icon-puzzle',
        url: `/stores/${storeId}/gateway`,
        tooltip: 'API Gateways',
      },
    ];

    if (!isPartner) {
      btns.push({
        icon: 'icon-lock',
        url: `/stores/${storeId}/credentials`,
        tooltip: 'Client Credentials',
      });
    }

    return (
      <div style={{position: 'absolute', right:'0px', paddingRight: '20px', verticalAlign: 'top' }}>
        {storeId && (
          <div>
            { btns.map((btn, bi) => (
              <Link key={bi} to={ btn.url }>
                <XMTooltipButton
                  key={bi}
                  icon={btn.icon}
                  tooltipTitle={btn.tooltip}
                  displayWarningIcon={btn.warning}
                />
              </Link>
            ))}
            <span>Store ID: {store.one.storeId}</span>
            <span className="ms-4"> URL:{' '}
              <a target="_blank" href={`https://${store.one.primaryDomain}`}
                 rel="noreferrer"> {store.one.primaryDomain}
              </a>
            </span>
            {!isPartner && (
              <span className="ms-4"> Gateway:{' '}
                <a target="_blank" href={gatewayBaseUrl + '/gateway/integrations/' + store.one.storeId + '.html?access_token='
                   + localStorage.getItem('jwtToken')} rel="noreferrer"> gateway
                </a>
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default BreadcrumbIcon;